import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';
import { cn } from '@shared/lib/cn';

interface MarqueeTextProps {
  children: ReactNode;
  as?: 'span' | 'p' | 'h1' | 'h2';
  className?: string;
}

/**
 * One-line text. When it does not fit, it scrolls once to show the rest, then
 * rests truncated with an ellipsis. Reduced motion skips the scroll.
 */
export function MarqueeText({ children, as: Tag = 'span', className }: MarqueeTextProps): JSX.Element {
  const textRef = useRef<HTMLSpanElement>(null);
  const [shift, setShift] = useState(0);
  const [done, setDone] = useState(false);
  // Live, not read once: the CSS stops the animation under reduced motion, so `animationend` would
  // never fire and the text would sit clipped with no ellipsis.
  const reduceMotion = useReducedMotion();

  // 1. Measure the overflow now and whenever the box resizes (width, text size).
  useLayoutEffect(() => {
    const text = textRef.current!;
    const measure = () => setShift(Math.max(0, Math.ceil(text.scrollWidth - text.clientWidth)));

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(text);
    return () => observer.disconnect();
  }, [children]);

  // 2. Scroll only while overflowing and not yet scrolled; otherwise truncate.
  const scrolling = shift > 1 && !done && !reduceMotion;
  const style = scrolling
    ? ({
        '--marquee-shift': `${shift}px`,
        '--marquee-duration': `${Math.max(4.2, Math.min(8.5, shift / 16 + 3.4))}s`,
      } as CSSProperties)
    : undefined;

  return (
    <Tag className={cn('block overflow-hidden', className)}>
      <span
        ref={textRef}
        className={cn('block whitespace-nowrap', scrolling ? 'ip4x-marquee' : 'truncate')}
        style={style}
        onAnimationEnd={() => setDone(true)}
      >
        {children}
      </span>
    </Tag>
  );
}
